/**
 * PeakGenerator — zoom-adaptive min/max peak extraction from AudioBuffer.
 *
 * Replaces the FFmpeg 800-peak waveform with detailed, zoom-responsive
 * peaks computed from the decoded AudioBuffer stored in IDB.
 */

export interface PeakData {
  /** Interleaved min/max pairs: [min0, max0, min1, max1, ...] */
  peaks: Float32Array;
  /** Number of source samples per peak pair */
  samplesPerPixel: number;
  /** Sample rate of source audio */
  sampleRate: number;
  /** Number of channels in source */
  channels: number;
  /** Total sample count */
  length: number;
}

/**
 * Extract min/max peaks from an AudioBuffer at a given samples-per-pixel resolution.
 * Returns interleaved [min, max, min, max, ...] pairs mixed down to mono.
 */
export function extractPeaks(audioBuffer: AudioBuffer, samplesPerPixel: number): PeakData {
  const channels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const numPeaks = Math.ceil(length / samplesPerPixel);
  const peaks = new Float32Array(numPeaks * 2);

  // Get channel data (mono mixdown)
  const channelData: Float32Array[] = [];
  for (let c = 0; c < channels; c++) {
    channelData.push(audioBuffer.getChannelData(c));
  }

  for (let i = 0; i < numPeaks; i++) {
    const start = i * samplesPerPixel;
    const end = Math.min(start + samplesPerPixel, length);

    let min = 1;
    let max = -1;

    for (let s = start; s < end; s++) {
      let sample = 0;
      for (let c = 0; c < channels; c++) {
        sample += channelData[c][s];
      }
      sample /= channels;

      if (sample < min) min = sample;
      if (sample > max) max = sample;
    }

    peaks[i * 2] = min;
    peaks[i * 2 + 1] = max;
  }

  return { peaks, samplesPerPixel, sampleRate: audioBuffer.sampleRate, channels, length };
}

/**
 * Generate peaks at a specific zoom level (pxPerMs).
 * Computes the ideal samplesPerPixel from the zoom and sample rate.
 */
export function generatePeaksAtZoom(
  audioBuffer: AudioBuffer,
  pxPerMs: number,
): PeakData {
  // pxPerMs → pixels per second → samples per pixel
  const pxPerSec = pxPerMs * 1000;
  const samplesPerPixel = Math.max(1, Math.floor(audioBuffer.sampleRate / pxPerSec));
  return extractPeaks(audioBuffer, samplesPerPixel);
}
